import { Injectable, Injector } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { SharedService } from '../shared/shared-service';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class ApiService {

  readonly ENCRYPTION_KEY = '4bX9z3vQN2WcE7YpJgHdLrMfTuXsaK1!';
  readonly IV_LENGTH = 16;
  readonly baseUrl = `${environment.baseUrl}/api/v1/`;
  readonly portalId: string = environment.environment;

  constructor(private http: HttpClient, private router: Router, private injector: Injector, private sharedService: SharedService) { }

  isAuthenticated(): boolean {
    const token = sessionStorage.getItem('access_token');

    if (!token) {
      return false;
    }

    const tokenExpiry = this.decodeTokenExpiry(token);
    if (tokenExpiry < Date.now()) {
      this.clearData();
      return false;
    }

    return true;
  }

  private decodeTokenExpiry(token: string): number {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  }

  postRequest(endpoint: string, data: any): Observable<any> {
    const options = {
      withCredentials: true
    };
    return this.http.post(this.baseUrl + endpoint, data, options);
  }

  putRequest(endpoint: string, data: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const options = {
      headers: headers,
      withCredentials: true
    };

    return this.http.put(this.baseUrl + endpoint, data, options)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  getRequest(endpoint: string): Observable<any> {
    const options = {
      withCredentials: true
    };
    return this.http.get(this.baseUrl + endpoint, options)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  getParamRequest(endpoint: string, params: any): Observable<any> {
    const options = {
      params: params,
      withCredentials: true
    };
    return this.http.get(this.baseUrl + endpoint, options)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  deleteRequest(endpoint: string, body: any, params?: any): Observable<any> {
    const options = {
      body: body,
      params: new HttpParams({ fromObject: params }),
      withCredentials: true
    };
    return this.http.request('DELETE', this.baseUrl + endpoint, options)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  deleteOneRequest(endpoint: string): Observable<any> {
    const options = {
      withCredentials: true
    };
    return this.http.request('DELETE', this.baseUrl + endpoint, options)
      .pipe(
        catchError(error => this.handleError(error))
      );
  }

  getStatus(endpoint: string): Observable<any> {
    return this.http.get(endpoint).pipe(
      catchError((error: any) => {
        if (error.status === 404) {
          console.log(`Endpoint not found: ${endpoint}`);
        } else {
          console.log(`Error fetching data from ${endpoint}:`, error);
        }
        return of(null);
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      errorMessage = `Server-side error: ${error.status} ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  encryptPayload(data: any): string {
    return this.encryption(JSON.stringify(data));
  }

  encryption(text: string): string {
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(this.ENCRYPTION_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    return (
      iv.toString(CryptoJS.enc.Hex) +
      ':' +
      encrypted.ciphertext.toString(CryptoJS.enc.Hex)
    );
  }

  clearData(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }
}
